import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  CheckCircle,
  Database,
  FileCheck,
  FileText,
  Mail,
  MessageCircle,
  PhoneCall,
  Sparkles,
  Users,
  Workflow,
  X,
  XCircle,
} from 'lucide-react';

import ContactForm from '../ContactForm';
import LanguageSwitcher from '../components/LanguageSwitcher';

const PageHeader = ({ onOpenPopup, c }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
          }}
        />
      </a>
      <div className="flex items-center gap-4 md:gap-8">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {c.headerCta}
        </button>
      </div>
    </div>
  </header>
);

const Pill = ({ icon: Icon, label, className }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold ${className}`}>
    <Icon size={16} />
    <span>{label}</span>
  </div>
);

const Tile = ({ icon: Icon, title, description, color }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-shadow duration-300 h-full">
    <div className={`p-3 rounded-xl w-fit mb-4 ${color.bg} ${color.text}`}>
      <Icon size={24} />
    </div>
    <div className="text-lg font-bold text-gray-900 mb-2">{title}</div>
    <div className="text-sm text-gray-600 leading-relaxed">{description}</div>
  </div>
);

const StepCard = ({ index, icon: Icon, title, description, flowLabel }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-7 h-full">
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[#141414] text-white flex items-center justify-center font-extrabold">
          {String(index).padStart(2, '0')}
        </div>
        <div className="p-3 rounded-2xl bg-gray-50 border border-gray-200 text-gray-900">
          <Icon size={22} />
        </div>
      </div>
      <div className="px-3 py-1 rounded-full text-xs font-bold border bg-amber-50 border-amber-200 text-amber-800">
        {flowLabel}
      </div>
    </div>
    <div className="mt-5 text-xl font-extrabold text-[#141414]">{title}</div>
    <div className="mt-3 text-sm text-gray-600 leading-relaxed">{description}</div>
  </div>
);

const MetricCard = ({ value, title, text, tone }) => (
  <div
    className={`rounded-3xl border p-6 bg-white ${
      tone === 'good' ? 'border-emerald-200' : tone === 'warn' ? 'border-amber-200' : 'border-gray-200'
    }`}
  >
    <div className="text-3xl font-extrabold text-[#141414]">{value}</div>
    <div className="mt-2 text-sm font-extrabold uppercase tracking-widest text-gray-500">{title}</div>
    <div className="mt-3 text-sm text-gray-700 leading-relaxed">{text}</div>
  </div>
);

const CompareRow = ({ label, traditional, bitrix }) => (
  <div>
    <div className="text-lg md:text-xl font-extrabold text-[#141414] mb-4 md:mb-3 px-1">{label}</div>
    <div className="grid grid-cols-1 lg:grid-cols-9 gap-4 items-stretch">
      <div className="lg:col-span-4 bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4">
        <div className="flex items-start gap-3">
          <XCircle size={18} className="text-gray-500 mt-0.5" />
          <div className="text-sm text-gray-700 leading-relaxed">{traditional}</div>
        </div>
      </div>
      <div className="lg:col-span-5 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4">
        <div className="flex items-start gap-3">
          <CheckCircle size={18} className="text-emerald-700 mt-0.5" />
          <div className="text-sm text-emerald-900 leading-relaxed">{bitrix}</div>
        </div>
      </div>
    </div>
  </div>
);

const FlowNode = ({ icon: Icon, title, caption, tone }) => (
  <div
    className={`rounded-3xl border p-6 bg-white ${
      tone === 'good' ? 'border-emerald-200' : tone === 'accent' ? 'border-amber-200' : 'border-gray-200'
    }`}
  >
    <div className="flex items-center gap-3">
      <div
        className={`p-3 rounded-2xl border ${
          tone === 'good'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
            : tone === 'accent'
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-gray-50 border-gray-200 text-gray-700'
        }`}
      >
        <Icon size={22} />
      </div>
      <div>
        <div className="text-sm font-extrabold text-[#141414]">{title}</div>
        <div className="text-sm text-gray-600 leading-relaxed">{caption}</div>
      </div>
    </div>
  </div>
);

const ReclutamientoPersonalPage = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const lang = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);

  const copy = {
    es: {
      headerCta: 'Agendar una Demostracion Gratis',
      popupTitle: 'Solicitar Diagnostico',
      popupDesc: 'Complete el formulario y le contactamos para coordinar la llamada.',
      back: 'Volver',
      flow: 'Flujo',
      pills: ['Bitrix24 + RR.HH.', 'IA para vacantes', 'Embudo visual'],
      heroTitle: 'Reclutamiento de Personal',
      heroSubtitle: 'Del requerimiento al onboarding en un solo flujo con Bitrix24.',
      heroPrimaryCta: 'Agendar una Demostracion Gratis',
      heroSecondaryCta: 'Ver el flujo',
      heroStats: [
        { title: 'Centralizacion', desc: 'Vacantes, candidatos y documentacion en un solo lugar.' },
        { title: 'Velocidad', desc: 'Aprobaciones y comunicacion sin friccion.' },
        { title: 'Calidad', desc: 'Menos errores y trazabilidad completa.' },
      ],
      quickMapTag: 'Mapa rapido',
      quickMapTitle: 'Tu flujo en 4 etapas',
      quickMapSteps: [
        { title: 'ADN del puesto', caption: 'Catalogo de puestos y criterios.' },
        { title: 'Solicitud aprobada', caption: 'Formularios y aprobaciones.' },
        { title: 'Vacante con IA', caption: 'Anuncio listo para publicar.' },
        { title: 'Embudo Kanban', caption: 'Etapas y control total.' },
      ],
      quickMapComm: 'Comunicacion registrada',
      quickMapOmni: 'Omnicanal',
      processTag: 'El proceso paso a paso',
      processTitle: 'De requerimiento a contratacion, sin burocracia',
      processDesc: 'Un ciclo completo y trazable para que RR.HH. deje de perseguir informacion y empiece a gestionar talento.',
      steps: [
        { title: 'Definicion del ADN del puesto', description: 'Antes de buscar, hay que saber que necesitamos. Administra un Catalogo de Puestos centralizado con detalles de aptitudes, rangos salariales y tareas especificas.' },
        { title: 'Solicitudes sin burocracia', description: 'El gerente de area llena un formulario y la solicitud vuela a su jefe para aprobacion digital. Sin llamadas, sin emails perdidos, sin hojas impresas.' },
        { title: 'IA que redacta por ti', description: 'Una vez aprobada la vacante, el sistema genera un anuncio creado con IA, optimizado y listo para publicar. Ahorra horas de redaccion creativa.' },
        { title: 'El embudo de seleccion', description: 'Recibe a los postulantes en un tablero visual (Kanban). Organiza cada etapa: desde la primera revision hasta el examen medico, manteniendo el control de la informacion.' },
      ],
      resultTag: 'Resultado',
      resultTitle: 'Un solo tablero, todo el equipo',
      resultDesc: 'Aprobaciones, Kanban, comunicaciones y documentos viven en la misma plataforma. Menos friccion y mas velocidad operativa.',
      resultChecks: ['Catalogo de puestos y estandares', 'Flujos de aprobacion auditable', 'Embudo con etapas claras', 'Documentos y PDFs consistentes'],
      resultBtn: 'Agendar demo',
      toolsTag: 'Herramientas de alto impacto',
      toolsTitle: 'El reclutamiento deja de ser manual',
      toolsDesc: 'Mas control, mejor experiencia del candidato y decisiones con datos, sin depender de personas o archivos.',
      tools: [
        { title: 'Omnicanalidad integrada', description: 'Contacta a candidatos por WhatsApp, telefono o correo directamente desde su ficha. Toda la comunicacion queda registrada.' },
        { title: 'Documentacion instantanea', description: 'Genera contratos, acuerdos de confidencialidad y cartas de compromiso en PDF con los datos del postulante.' },
        { title: 'Scoring y ternas', description: 'Clasifica a los mejores perfiles segun tus criterios y presenta al gerente solicitante una terna de finalistas lista para entrevista.' },
        { title: 'Base de datos del manana', description: 'Los candidatos que no avanzaron hoy se convierten en tu reserva de talento. Almacenalos y clasificalos para futuras vacantes.' },
      ],
      onboardingTag: 'Onboarding perfecto',
      onboardingTitle: 'El proceso no termina con la firma',
      onboardingDesc: 'Al marcar a un candidato como contratado, Bitrix24 activa el engranaje de la empresa para que todo este listo antes del primer dia.',
      flowNodes: [
        { title: 'RR.HH. marca: Contratado', caption: 'Se cierra el proceso de seleccion y se activa el onboarding.' },
        { title: 'Contabilidad', caption: 'Recibe el aviso para el alta en nomina.' },
        { title: 'TI y Sistemas', caption: 'Lista de implementos: laptop, accesos y software segun catalogo de puestos.' },
      ],
      objectiveTag: 'Objetivo',
      objectiveTitle: 'Todo listo, antes de su escritorio',
      objectiveDesc: 'Tareas automaticas, checklist por rol y comunicacion interna sin perder informacion. Un onboarding ordenado mejora productividad y experiencia desde el dia 1.',
      objectiveCard1Title: 'Comunicacion interna',
      objectiveCard1Desc: 'Notificaciones y tareas para cada area sin depender de recordatorios.',
      objectiveCard2Title: 'Coordinacion total',
      objectiveCard2Desc: 'Todo el historial del candidato queda disponible para RR.HH. y jefaturas.',
      onboardingBtn: 'Quiero optimizar mi onboarding',
      footerNote: 'Configuracion personalizada segun las necesidades de tu empresa.',
      compareTag: 'El salto de calidad: tradicional vs. inteligente',
      compareTitle: 'Del caos manual a una estructura automatizada',
      compareDesc: 'No es solo software: es una nueva forma de hacer crecer tu equipo con trazabilidad, velocidad y control.',
      compareTraditional: 'Metodo Tradicional (Email y Papel)',
      compareBitrix: 'Proceso con Bitrix24',
      comparisonRows: [
        { label: 'Solicitud de Vacante', traditional: 'Emails que se pierden o llamadas informales.', bitrix: 'Formulario estandarizado con flujo de aprobacion digital.' },
        { label: 'Creacion de Anuncios', traditional: 'Horas redactando y revisando perfiles.', bitrix: 'IA integrada que genera el anuncio en segundos.' },
        { label: 'Gestion de Candidatos', traditional: 'Carpetas con CVs impresos o archivos en Excel.', bitrix: 'Embudo visual (CRM) con toda la info centralizada.' },
        { label: 'Comunicacion', traditional: 'Telefonos personales y bandejas de entrada saturadas.', bitrix: 'Omnicanalidad: WhatsApp y Correo desde el sistema.' },
        { label: 'Documentacion', traditional: 'Redaccion manual de contratos (riesgo de error).', bitrix: 'Generacion automatica de PDFs con datos del proceso.' },
        { label: 'Onboarding', traditional: 'TI y Contabilidad se enteran el dia que el empleado llega.', bitrix: 'Notificaciones automaticas y tareas listas para cada area.' },
        { label: 'Base de Datos', traditional: 'Los CVs no seleccionados terminan olvidados.', bitrix: 'Base de talento activa para futuras busquedas.' },
      ],
      impactTag: 'Por que este cambio es vital?',
      impactTitle: 'Impacto operativo y humano',
      impactDesc: 'Cuando el reclutamiento se vuelve trazable, baja la friccion interna y mejora la experiencia del candidato.',
      impactChecks: ['Menos tiempo persiguiendo aprobaciones y documentos.', 'Candidatos con respuestas rapidas y profesionales.', 'Trazabilidad total para auditoria y mejora continua.'],
      metric1Title: 'Tiempo de contratacion',
      metric1Text: 'Reduccion en el tiempo de contratacion con flujos y tablero central.',
      metric2Title: 'Errores en documentos',
      metric2Text: 'Eliminacion de errores en contratos y acuerdos con generacion automatica.',
      metric3Title: 'Marca empleadora',
      metric3Text: 'Respuestas rapidas y profesionalismo elevan la percepcion del candidato.',
      finalTag: 'Flexibilidad total',
      finalTitle: 'Tu empresa es unica. Tu software tambien debe serlo.',
      finalDesc: 'Optimizamos tus pasos actuales, personalizamos formularios y automatizamos reglas de negocio especificas con la potencia de Bitrix24.',
      finalPills: ['Flujos a medida', 'Formularios personalizados', 'Metricas y control'],
      finalCardTag: 'Cierre',
      finalCardTitle: 'Reclutamiento de Personal',
      finalCardDesc: 'Deja de gestionar archivos y empieza a gestionar talento.',
      finalBtn: 'Quiero optimizar mi proceso ahora',
    },
    en: {
      headerCta: 'Schedule a Free Demo',
      popupTitle: 'Request Diagnostic',
      popupDesc: 'Complete the form and we will contact you to schedule the call.',
      back: 'Back',
      flow: 'Flow',
      pills: ['Bitrix24 + HR', 'AI for vacancies', 'Visual pipeline'],
      heroTitle: 'Recruitment',
      heroSubtitle: 'From request to onboarding in a single flow with Bitrix24.',
      heroPrimaryCta: 'Schedule a Free Demo',
      heroSecondaryCta: 'See the flow',
      heroStats: [
        { title: 'Centralization', desc: 'Vacancies, candidates, and documents in one place.' },
        { title: 'Speed', desc: 'Approvals and communication with no friction.' },
        { title: 'Quality', desc: 'Fewer errors and full traceability.' },
      ],
      quickMapTag: 'Quick map',
      quickMapTitle: 'Your flow in 4 stages',
      quickMapSteps: [
        { title: 'Role DNA', caption: 'Role catalog and criteria.' },
        { title: 'Approved request', caption: 'Forms and approvals.' },
        { title: 'AI vacancy', caption: 'Ready-to-publish ad.' },
        { title: 'Kanban pipeline', caption: 'Stages and total control.' },
      ],
      quickMapComm: 'Logged communication',
      quickMapOmni: 'Omnichannel',
      processTag: 'Step-by-step process',
      processTitle: 'From request to hiring, without bureaucracy',
      processDesc: 'A complete and traceable cycle so HR stops chasing information and starts managing talent.',
      steps: [
        { title: 'Define role DNA', description: 'Before searching, define what you need. Manage a centralized role catalog with skills, salary ranges, and specific tasks.' },
        { title: 'No-bureaucracy requests', description: 'The area manager fills out a form and the request goes to the supervisor for digital approval. No calls, lost emails, or printed sheets.' },
        { title: 'AI writes for you', description: 'Once the vacancy is approved, the system generates an AI-created ad, optimized and ready to publish.' },
        { title: 'Selection pipeline', description: 'Receive applicants in a visual Kanban board. Organize each stage from first review to medical exam with full control.' },
      ],
      resultTag: 'Result',
      resultTitle: 'One board, the whole team',
      resultDesc: 'Approvals, Kanban, communications, and documents live in one platform. Less friction and more operational speed.',
      resultChecks: ['Role catalog and standards', 'Auditable approval flows', 'Pipeline with clear stages', 'Consistent documents and PDFs'],
      resultBtn: 'Schedule demo',
      toolsTag: 'High-impact tools',
      toolsTitle: 'Recruitment stops being manual',
      toolsDesc: 'More control, better candidate experience, and data-driven decisions.',
      tools: [
        { title: 'Integrated omnichannel', description: 'Contact candidates via WhatsApp, phone, or email directly from their profile.' },
        { title: 'Instant documentation', description: 'Generate contracts, NDAs, and commitment letters in PDF with candidate data.' },
        { title: 'Scoring and shortlists', description: 'Rank the best profiles and present ready-to-interview finalists.' },
        { title: 'Future talent database', description: 'Unselected candidates become your talent pool for future vacancies.' },
      ],
      onboardingTag: 'Perfect onboarding',
      onboardingTitle: 'The process does not end with signature',
      onboardingDesc: 'When a candidate is marked as hired, Bitrix24 activates internal tasks so everything is ready before day one.',
      flowNodes: [
        { title: 'HR marks: Hired', caption: 'Selection closes and onboarding starts.' },
        { title: 'Accounting', caption: 'Receives payroll onboarding notice.' },
        { title: 'IT and Systems', caption: 'Equipment and access checklist according to role catalog.' },
      ],
      objectiveTag: 'Goal',
      objectiveTitle: 'Everything ready before the first desk day',
      objectiveDesc: 'Automatic tasks, role-based checklists, and internal communication without losing information.',
      objectiveCard1Title: 'Internal communication',
      objectiveCard1Desc: 'Notifications and tasks for each area without manual reminders.',
      objectiveCard2Title: 'Total coordination',
      objectiveCard2Desc: 'Candidate history remains available for HR and managers.',
      onboardingBtn: 'I want to optimize onboarding',
      footerNote: 'Custom setup based on your company needs.',
      compareTag: 'Quality leap: traditional vs smart',
      compareTitle: 'From manual chaos to automated structure',
      compareDesc: 'Not only software: a new way to grow your team with traceability and control.',
      compareTraditional: 'Traditional method (Email and paper)',
      compareBitrix: 'Bitrix24 process',
      comparisonRows: [
        { label: 'Vacancy request', traditional: 'Lost emails or informal calls.', bitrix: 'Standard form with digital approvals.' },
        { label: 'Ad creation', traditional: 'Hours writing and reviewing profiles.', bitrix: 'Built-in AI creates the ad in seconds.' },
        { label: 'Candidate management', traditional: 'Printed CV folders or Excel files.', bitrix: 'Visual pipeline (CRM) with centralized info.' },
        { label: 'Communication', traditional: 'Personal phones and saturated inboxes.', bitrix: 'Omnichannel: WhatsApp and email from system.' },
        { label: 'Documentation', traditional: 'Manual contract drafting (error risk).', bitrix: 'Automatic PDF generation with process data.' },
        { label: 'Onboarding', traditional: 'IT and Accounting hear about it on start day.', bitrix: 'Automatic notifications and tasks by area.' },
        { label: 'Database', traditional: 'Unselected CVs get forgotten.', bitrix: 'Active talent base for future searches.' },
      ],
      impactTag: 'Why this change matters',
      impactTitle: 'Operational and human impact',
      impactDesc: 'When recruitment becomes traceable, internal friction drops and candidate experience improves.',
      impactChecks: ['Less time chasing approvals and documents.', 'Faster, professional candidate responses.', 'Full traceability for audits and improvement.'],
      metric1Title: 'Hiring time',
      metric1Text: 'Reduction in hiring time with workflows and centralized board.',
      metric2Title: 'Document errors',
      metric2Text: 'Elimination of errors in contracts with automated generation.',
      metric3Title: 'Employer brand',
      metric3Text: 'Fast responses and professionalism improve candidate perception.',
      finalTag: 'Full flexibility',
      finalTitle: 'Your company is unique. Your software should be too.',
      finalDesc: 'We optimize your current steps and automate specific business rules with Bitrix24.',
      finalPills: ['Custom workflows', 'Custom forms', 'Metrics and control'],
      finalCardTag: 'Closing',
      finalCardTitle: 'Recruitment',
      finalCardDesc: 'Stop managing files and start managing talent.',
      finalBtn: 'I want to optimize my process now',
    },
    pt: {
      headerCta: 'Agendar uma Demo Gratis',
      popupTitle: 'Solicitar Diagnostico',
      popupDesc: 'Preencha o formulario e entraremos em contato para agendar a chamada.',
      back: 'Voltar',
      flow: 'Fluxo',
      pills: ['Bitrix24 + RH', 'IA para vagas', 'Funil visual'],
      heroTitle: 'Recrutamento de Pessoal',
      heroSubtitle: 'Do pedido ao onboarding em um unico fluxo com Bitrix24.',
      heroPrimaryCta: 'Agendar uma Demo Gratis',
      heroSecondaryCta: 'Ver o fluxo',
      heroStats: [
        { title: 'Centralizacao', desc: 'Vagas, candidatos e documentos em um unico lugar.' },
        { title: 'Velocidade', desc: 'Aprovacoes e comunicacao sem friccao.' },
        { title: 'Qualidade', desc: 'Menos erros e rastreabilidade completa.' },
      ],
      quickMapTag: 'Mapa rapido',
      quickMapTitle: 'Seu fluxo em 4 etapas',
      quickMapSteps: [
        { title: 'DNA da vaga', caption: 'Catalogo de cargos e criterios.' },
        { title: 'Solicitacao aprovada', caption: 'Formularios e aprovacoes.' },
        { title: 'Vaga com IA', caption: 'Anuncio pronto para publicar.' },
        { title: 'Funil Kanban', caption: 'Etapas e controle total.' },
      ],
      quickMapComm: 'Comunicacao registrada',
      quickMapOmni: 'Omnicanal',
      processTag: 'Processo passo a passo',
      processTitle: 'Do pedido a contratacao, sem burocracia',
      processDesc: 'Um ciclo completo e rastreavel para que RH pare de perseguir informacao e comece a gerir talento.',
      steps: [
        { title: 'Definicao do DNA da vaga', description: 'Antes de buscar, e preciso saber o que precisamos. Administre um catalogo de cargos centralizado com aptidoes, faixa salarial e tarefas.' },
        { title: 'Solicitacoes sem burocracia', description: 'O gestor preenche um formulario e a solicitacao segue para aprovacao digital.' },
        { title: 'IA que escreve por voce', description: 'Com a vaga aprovada, o sistema gera um anuncio com IA pronto para publicar.' },
        { title: 'Funil de selecao', description: 'Receba candidatos em um quadro Kanban visual e acompanhe todas as etapas.' },
      ],
      resultTag: 'Resultado',
      resultTitle: 'Um unico painel, todo o time',
      resultDesc: 'Aprovacoes, Kanban, comunicacoes e documentos na mesma plataforma.',
      resultChecks: ['Catalogo de cargos e padroes', 'Fluxos de aprovacao auditaveis', 'Funil com etapas claras', 'Documentos e PDFs consistentes'],
      resultBtn: 'Agendar demo',
      toolsTag: 'Ferramentas de alto impacto',
      toolsTitle: 'O recrutamento deixa de ser manual',
      toolsDesc: 'Mais controle, melhor experiencia do candidato e decisoes com dados.',
      tools: [
        { title: 'Omnicanalidade integrada', description: 'Contate candidatos por WhatsApp, telefone ou e-mail direto da ficha.' },
        { title: 'Documentacao instantanea', description: 'Gere contratos e cartas em PDF com dados do candidato.' },
        { title: 'Scoring e shortlist', description: 'Classifique perfis e apresente finalistas prontos para entrevista.' },
        { title: 'Base de talentos futura', description: 'Candidatos nao aprovados hoje viram reserva para futuras vagas.' },
      ],
      onboardingTag: 'Onboarding perfeito',
      onboardingTitle: 'O processo nao termina com a assinatura',
      onboardingDesc: 'Quando o candidato e marcado como contratado, o Bitrix24 ativa as tarefas internas antes do primeiro dia.',
      flowNodes: [
        { title: 'RH marca: Contratado', caption: 'Selecao encerrada e onboarding iniciado.' },
        { title: 'Contabilidade', caption: 'Recebe aviso para inclusao na folha.' },
        { title: 'TI e Sistemas', caption: 'Checklist de equipamentos e acessos por cargo.' },
      ],
      objectiveTag: 'Objetivo',
      objectiveTitle: 'Tudo pronto antes da primeira mesa',
      objectiveDesc: 'Tarefas automaticas, checklist por funcao e comunicacao interna sem perda de informacao.',
      objectiveCard1Title: 'Comunicacao interna',
      objectiveCard1Desc: 'Notificacoes e tarefas por area sem lembretes manuais.',
      objectiveCard2Title: 'Coordenacao total',
      objectiveCard2Desc: 'Historico do candidato disponivel para RH e liderancas.',
      onboardingBtn: 'Quero otimizar meu onboarding',
      footerNote: 'Configuracao personalizada conforme as necessidades da empresa.',
      compareTag: 'Salto de qualidade: tradicional vs inteligente',
      compareTitle: 'Do caos manual para estrutura automatizada',
      compareDesc: 'Nao e so software: e uma nova forma de crescer com rastreabilidade e controle.',
      compareTraditional: 'Metodo Tradicional (Email e papel)',
      compareBitrix: 'Processo com Bitrix24',
      comparisonRows: [
        { label: 'Solicitacao de vaga', traditional: 'Emails perdidos ou chamadas informais.', bitrix: 'Formulario padrao com aprovacao digital.' },
        { label: 'Criacao de anuncios', traditional: 'Horas escrevendo e revisando perfis.', bitrix: 'IA integrada gera o anuncio em segundos.' },
        { label: 'Gestao de candidatos', traditional: 'CVs impressos ou planilhas.', bitrix: 'Funil visual com informacao centralizada.' },
        { label: 'Comunicacao', traditional: 'Telefones pessoais e caixas lotadas.', bitrix: 'Omnicanal: WhatsApp e email no sistema.' },
        { label: 'Documentacao', traditional: 'Contratos manuais com risco de erro.', bitrix: 'PDFs gerados automaticamente.' },
        { label: 'Onboarding', traditional: 'TI e Contabilidade so sabem no dia de entrada.', bitrix: 'Notificacoes e tarefas automaticas por area.' },
        { label: 'Base de dados', traditional: 'CVs nao selecionados ficam esquecidos.', bitrix: 'Base ativa para futuras buscas.' },
      ],
      impactTag: 'Por que essa mudanca e vital?',
      impactTitle: 'Impacto operacional e humano',
      impactDesc: 'Quando o recrutamento se torna rastreavel, a friccao interna cai e a experiencia melhora.',
      impactChecks: ['Menos tempo perseguindo aprovacoes e documentos.', 'Respostas mais rapidas e profissionais.', 'Rastreabilidade total para auditoria e melhoria continua.'],
      metric1Title: 'Tempo de contratacao',
      metric1Text: 'Reducao no tempo de contratacao com fluxos e painel central.',
      metric2Title: 'Erros em documentos',
      metric2Text: 'Eliminacao de erros em contratos com geracao automatica.',
      metric3Title: 'Marca empregadora',
      metric3Text: 'Respostas rapidas e profissionalismo elevam a percepcao do candidato.',
      finalTag: 'Flexibilidade total',
      finalTitle: 'Sua empresa e unica. Seu software tambem.',
      finalDesc: 'Otimizamos seus passos atuais e automatizamos regras especificas com Bitrix24.',
      finalPills: ['Fluxos sob medida', 'Formularios personalizados', 'Metricas e controle'],
      finalCardTag: 'Fechamento',
      finalCardTitle: 'Recrutamento de Pessoal',
      finalCardDesc: 'Pare de gerenciar arquivos e comecar a gerir talentos.',
      finalBtn: 'Quero otimizar meu processo agora',
    },
  };
  const c = copy[lang] || copy.es;

  const heroImageUrl = '/images/recursos-humanos/recursos-humanos.png';

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  const steps = useMemo(
    () => [
      {
        icon: FileCheck,
        title: c.steps[0].title,
        description: c.steps[0].description,
      },
      {
        icon: Workflow,
        title: c.steps[1].title,
        description: c.steps[1].description,
      },
      {
        icon: Sparkles,
        title: c.steps[2].title,
        description: c.steps[2].description,
      },
      {
        icon: Users,
        title: c.steps[3].title,
        description: c.steps[3].description,
      },
    ],
    [c]
  );

  const tools = useMemo(
    () => [
      {
        icon: MessageCircle,
        title: c.tools[0].title,
        description: c.tools[0].description,
        color: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
      },
      {
        icon: FileText,
        title: c.tools[1].title,
        description: c.tools[1].description,
        color: { bg: 'bg-amber-50', text: 'text-amber-700' },
      },
      {
        icon: BarChart3,
        title: c.tools[2].title,
        description: c.tools[2].description,
        color: { bg: 'bg-blue-50', text: 'text-blue-700' },
      },
      {
        icon: Database,
        title: c.tools[3].title,
        description: c.tools[3].description,
        color: { bg: 'bg-gray-50', text: 'text-gray-700' },
      },
    ],
    [c]
  );

  const comparisonRows = useMemo(
    () => [
      {
        label: c.comparisonRows[0].label,
        traditional: c.comparisonRows[0].traditional,
        bitrix: c.comparisonRows[0].bitrix,
      },
      {
        label: c.comparisonRows[1].label,
        traditional: c.comparisonRows[1].traditional,
        bitrix: c.comparisonRows[1].bitrix,
      },
      {
        label: c.comparisonRows[2].label,
        traditional: c.comparisonRows[2].traditional,
        bitrix: c.comparisonRows[2].bitrix,
      },
      {
        label: c.comparisonRows[3].label,
        traditional: c.comparisonRows[3].traditional,
        bitrix: c.comparisonRows[3].bitrix,
      },
      {
        label: c.comparisonRows[4].label,
        traditional: c.comparisonRows[4].traditional,
        bitrix: c.comparisonRows[4].bitrix,
      },
      {
        label: c.comparisonRows[5].label,
        traditional: c.comparisonRows[5].traditional,
        bitrix: c.comparisonRows[5].bitrix,
      },
      {
        label: c.comparisonRows[6].label,
        traditional: c.comparisonRows[6].traditional,
        bitrix: c.comparisonRows[6].bitrix,
      },
    ],
    [c]
  );

  return (
    <div className="relative min-h-screen bg-transparent">
      {isPopupOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePopup();
          }}
        >
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('common.back')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{c.popupTitle}</h3>
              <p className="text-gray-600 mb-6">{c.popupDesc}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={openPopup} c={c} />

      <main className="pb-20">
        <section className="relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-20">
          <div className="relative overflow-hidden bg-[#0B0B0B] min-h-[85vh]">
            <div className="absolute top-0 left-0 right-0 z-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) navigate(-1);
                    else navigate('/');
                  }}
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white transition font-semibold bg-white/10 border border-white/15 backdrop-blur px-4 py-2 rounded-full"
                >
                  <ArrowLeft size={16} />
                  {c.back}
                </button>
              </div>
            </div>

            <div className="absolute inset-0">
              <img
                src={heroImageUrl}
                alt="Reclutamiento de Personal"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/2400x1600/0b0b0b/ffffff?text=Reclutamiento';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-20">
              <div className="grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                  <div className="flex flex-wrap gap-2">
                    <Pill
                      icon={Workflow}
                      label={c.pills[0]}
                      className="bg-white/10 border-white/15 text-white"
                    />
                    <Pill
                      icon={Sparkles}
                      label={c.pills[1]}
                      className="bg-amber-500/10 border-amber-500/25 text-amber-200"
                    />
                    <Pill
                      icon={BarChart3}
                      label={c.pills[2]}
                      className="bg-emerald-500/10 border-emerald-500/25 text-emerald-200"
                    />
                  </div>

                  <h1 className="mt-6 text-4xl md:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                    {c.heroTitle}
                  </h1>
                  <p className="mt-6 text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl">
                    {c.heroSubtitle}
                  </p>

                  <div className="mt-10 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={openPopup}
                      className="inline-flex items-center justify-center gap-2 bg-white text-[#141414] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition"
                    >
                      {c.heroPrimaryCta} <ArrowRight size={18} />
                    </button>
                    <a
                      href="#proceso"
                      className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/15 transition"
                    >
                      {c.heroSecondaryCta} <ArrowDown size={18} />
                    </a>
                  </div>

                  <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-2xl">
                    <div className="bg-white/10 border border-white/15 rounded-2xl p-4">
                      <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{c.heroStats[0].title}</div>
                      <div className="mt-2 text-sm text-white/80">{c.heroStats[0].desc}</div>
                    </div>
                    <div className="bg-white/10 border border-white/15 rounded-2xl p-4">
                      <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{c.heroStats[1].title}</div>
                      <div className="mt-2 text-sm text-white/80">{c.heroStats[1].desc}</div>
                    </div>
                    <div className="bg-white/10 border border-white/15 rounded-2xl p-4">
                      <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{c.heroStats[2].title}</div>
                      <div className="mt-2 text-sm text-white/80">{c.heroStats[2].desc}</div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="bg-white/10 border border-white/15 backdrop-blur rounded-3xl p-7">
                    <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{c.quickMapTag}</div>
                    <div className="mt-3 text-2xl font-extrabold text-white">{c.quickMapTitle}</div>
                    <div className="mt-6 space-y-3">
                      {[
                        { icon: FileCheck, title: c.quickMapSteps[0].title, caption: c.quickMapSteps[0].caption },
                        { icon: Workflow, title: c.quickMapSteps[1].title, caption: c.quickMapSteps[1].caption },
                        { icon: Sparkles, title: c.quickMapSteps[2].title, caption: c.quickMapSteps[2].caption },
                        { icon: Users, title: c.quickMapSteps[3].title, caption: c.quickMapSteps[3].caption },
                      ].map((it) => (
                        <div key={it.title} className="flex items-start gap-3 bg-black/20 border border-white/10 rounded-2xl p-4">
                          <div className="p-3 rounded-2xl bg-white/10 border border-white/15 text-white">
                            <it.icon size={20} />
                          </div>
                          <div>
                            <div className="text-sm font-extrabold text-white">{it.title}</div>
                            <div className="text-sm text-white/70 leading-relaxed">{it.caption}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
                      <div className="flex items-center gap-2 text-white/80 text-sm font-semibold">
                        <Mail size={18} />
                        <span>{c.quickMapComm}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/80 text-sm font-semibold">
                        <PhoneCall size={18} />
                        <span>{c.quickMapOmni}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="proceso" className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center">
            <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{c.processTag}</div>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-[#141414]">
              {c.processTitle}
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              {c.processDesc}
            </p>
          </div>

          <div className="mt-12 grid lg:grid-cols-12 gap-6 items-stretch">
            <div className="lg:col-span-8 grid md:grid-cols-2 gap-6">
              {steps.map((s, idx) => (
                <StepCard key={s.title} index={idx + 1} icon={s.icon} title={s.title} description={s.description} flowLabel={c.flow} />
              ))}
            </div>
            <div className="lg:col-span-4">
              <div className="bg-white border border-gray-200 rounded-3xl p-7 h-full">
                <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{c.resultTag}</div>
                <div className="mt-4 text-2xl font-extrabold text-[#141414]">{c.resultTitle}</div>
                <div className="mt-4 text-sm text-gray-600 leading-relaxed">
                  {c.resultDesc}
                </div>
                <div className="mt-6 space-y-3">
                  {[
                    { icon: CheckCircle, text: c.resultChecks[0] },
                    { icon: CheckCircle, text: c.resultChecks[1] },
                    { icon: CheckCircle, text: c.resultChecks[2] },
                    { icon: CheckCircle, text: c.resultChecks[3] },
                  ].map((it) => (
                    <div key={it.text} className="flex items-start gap-3">
                      <it.icon size={18} className="text-emerald-700 mt-0.5" />
                      <div className="text-sm text-gray-700 leading-relaxed">{it.text}</div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={openPopup}
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-900 transition"
                >
                  {c.resultBtn} <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="text-center">
            <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{c.toolsTag}</div>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-[#141414]">
              {c.toolsTitle}
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              {c.toolsDesc}
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tools.map((t) => (
              <Tile key={t.title} icon={t.icon} title={t.title} description={t.description} color={t.color} />
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-6">
                <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{c.onboardingTag}</div>
                <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight text-[#141414]">
                  {c.onboardingTitle}
                </h2>
                <p className="mt-5 text-gray-600 text-lg leading-relaxed">
                  {c.onboardingDesc}
                </p>

                <div className="mt-8 grid gap-4">
                  <FlowNode
                    icon={Users}
                    title={c.flowNodes[0].title}
                    caption={c.flowNodes[0].caption}
                    tone="accent"
                  />
                  <div className="flex items-center justify-center py-1">
                    <ArrowDown size={18} className="text-gray-300" />
                  </div>
                  <FlowNode
                    icon={FileCheck}
                    title={c.flowNodes[1].title}
                    caption={c.flowNodes[1].caption}
                    tone="good"
                  />
                  <div className="flex items-center justify-center py-1">
                    <ArrowDown size={18} className="text-gray-300" />
                  </div>
                  <FlowNode
                    icon={Workflow}
                    title={c.flowNodes[2].title}
                    caption={c.flowNodes[2].caption}
                    tone="good"
                  />
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="rounded-3xl border border-gray-200 bg-gray-50 p-8">
                  <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{c.objectiveTag}</div>
                  <div className="mt-4 text-2xl font-extrabold text-[#141414]">{c.objectiveTitle}</div>
                  <div className="mt-4 text-sm text-gray-700 leading-relaxed">
                    {c.objectiveDesc}
                  </div>

                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <div className="bg-white border border-gray-200 rounded-2xl p-5">
                      <div className="flex items-center gap-2 text-sm font-extrabold text-[#141414]">
                        <Mail size={18} />
                        {c.objectiveCard1Title}
                      </div>
                      <div className="mt-3 text-sm text-gray-600 leading-relaxed">
                        {c.objectiveCard1Desc}
                      </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-2xl p-5">
                      <div className="flex items-center gap-2 text-sm font-extrabold text-[#141414]">
                        <PhoneCall size={18} />
                        {c.objectiveCard2Title}
                      </div>
                      <div className="mt-3 text-sm text-gray-600 leading-relaxed">
                        {c.objectiveCard2Desc}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={openPopup}
                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-900 transition"
                  >
                    {c.onboardingBtn} <ArrowRight size={18} />
                  </button>
                  <div className="mt-3 text-xs text-gray-500 text-center">
                    {c.footerNote}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="text-center">
            <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">
              {c.compareTag}
            </div>
            <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-[#141414]">
              {c.compareTitle}
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              {c.compareDesc}
            </p>
          </div>

          <div className="mt-10 bg-white border border-gray-200 rounded-3xl p-6 md:p-8">
            <div className="hidden lg:grid grid-cols-9 gap-4 mb-6">
              <div className="col-span-4">
                <div className="bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4">
                  <div className="text-sm font-extrabold text-gray-900">{c.compareTraditional}</div>
                </div>
              </div>
              <div className="col-span-5">
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4">
                  <div className="text-sm font-extrabold text-emerald-900">{c.compareBitrix}</div>
                </div>
              </div>
            </div>

            <div className="grid gap-6 md:gap-4">
              {comparisonRows.map((r) => (
                <CompareRow key={r.label} label={r.label} traditional={r.traditional} bitrix={r.bitrix} />
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-6">
                <div className="text-xs font-extrabold uppercase tracking-widest text-gray-500">{c.impactTag}</div>
                <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight text-[#141414]">
                  {c.impactTitle}
                </h2>
                <p className="mt-5 text-gray-600 text-lg leading-relaxed">
                  {c.impactDesc}
                </p>

                <div className="mt-8 grid gap-3">
                  {[
                    c.impactChecks[0],
                    c.impactChecks[1],
                    c.impactChecks[2],
                  ].map((it) => (
                    <div key={it} className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-emerald-700 mt-0.5" />
                      <div className="text-sm text-gray-700 leading-relaxed">{it}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-6 grid sm:grid-cols-3 gap-4">
                <MetricCard
                  value="−40%"
                  title={c.metric1Title}
                  text={c.metric1Text}
                  tone="good"
                />
                <MetricCard
                  value="0"
                  title={c.metric2Title}
                  text={c.metric2Text}
                  tone="warn"
                />
                <MetricCard
                  value="Mejor"
                  title={c.metric3Title}
                  text={c.metric3Text}
                  tone="neutral"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20">
          <div className="bg-[#141414] rounded-3xl p-9 md:p-12 text-white overflow-hidden relative">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-emerald-500/20 blur-2xl" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{c.finalTag}</div>
                <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight">
                  {c.finalTitle}
                </h2>
                <p className="mt-5 text-white/75 text-lg leading-relaxed">
                  {c.finalDesc}
                </p>
                <div className="mt-7 flex flex-wrap gap-2">
                  <Pill icon={Workflow} label={c.finalPills[0]} className="bg-white/10 border-white/15 text-white" />
                  <Pill icon={FileText} label={c.finalPills[1]} className="bg-white/10 border-white/15 text-white" />
                  <Pill icon={BarChart3} label={c.finalPills[2]} className="bg-white/10 border-white/15 text-white" />
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="bg-white/10 border border-white/15 rounded-3xl p-7">
                  <div className="text-xs font-extrabold uppercase tracking-widest text-white/70">{c.finalCardTag}</div>
                  <div className="mt-4 text-2xl font-extrabold">{c.finalCardTitle}</div>
                  <div className="mt-3 text-white/75 leading-relaxed">
                    {c.finalCardDesc}
                  </div>
                  <button
                    onClick={openPopup}
                    className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-white text-[#141414] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition"
                  >
                    {c.finalBtn} <ArrowRight size={18} />
                  </button>
                  <div className="mt-3 text-xs text-white/60 text-center">
                    {c.footerNote}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ReclutamientoPersonalPage;

